import React, {useCallback} from 'react';

import {observer} from 'mobx-react';

import {useStores} from '../../../Hooks';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import SettingsModalShell from '../SettingsShared/SettingsModalShell';
import NameField from '../SettingsShared/NameField';
import PrivacySection from '../SettingsShared/PrivacySection';
import useFolderSettings from './useFolderSettings';
import {openDeleteFolderConfirm} from '../DeleteFolderModal';

const PRIVATE_COPY = {
    publicBanner: {title: 'Project-Wide Access', body: 'All members with project access can view and interact with this folder'},
    privateBanner: {title: 'Private Access', body: 'Only invited members can access this folder'},
};

const FolderSettingsModal = observer(() => {
    const {modalStore, generalStore} = useStores();
    const settings = useFolderSettings();
    const isAdminOrOwner = generalStore.userIsWorkspaceAdminOrOwner();
    const isMirrored = !!settings.folder?.importedFromWorkspace;

    const handleEnter = useCallback(() => {
        const f = modalStore.modalData?.folder;
        if (!f) return;
        settings.initFromFolder(f);
    }, [modalStore.modalData?.folder]);

    const closeModal = useCallback(() => {
        modalStore.isFolderSettingsModalOpen = false;
        modalStore.resetModalData();
    }, [modalStore]);

    const handleDelete = () => {
        const f = modalStore.modalData?.folder;
        if (!f) return;
        closeModal();
        openDeleteFolderConfirm(f);
    };

    return (
        <SettingsModalShell
            openControl="isFolderSettingsModalOpen"
            title="Folder Settings"
            subtitle={isMirrored ? 'Configure folder access' : 'Configure folder properties and access'}
            onEnter={handleEnter}
            isDirty={settings.isDirty}
            onSave={settings.save}
            onDiscard={settings.discard}
            onDelete={isMirrored ? null : handleDelete}
            closeModal={closeModal}
            compact
        >
            <ConditionalComponent if={!isMirrored}>
                <div className="settings-section">
                    <NameField value={settings.editedName} onChange={settings.setEditedName} originalValue={settings.originalName} limit={200} />
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={isAdminOrOwner && !!settings.itemId}>
                <div className="settings-section">
                    <PrivacySection settings={settings} privateCopy={PRIVATE_COPY} />
                </div>
            </ConditionalComponent>
        </SettingsModalShell>
    );
});

export default FolderSettingsModal;
